import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Grid,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { useTranslation } from 'react-i18next';
import { fetchRestaurantInfo, updateRestaurantInfo } from '../restaurantApi';
import config from '../config';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Restaurant() {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phoneNumber: '',
    email: '',
    aboutUs: '',
    ourVision: '',
    whatWeProvide: '',
    whyUs: '',
    logoPath: '',
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: ''
  });
  const [additionalPhones, setAdditionalPhones] = useState([]);
  const [newPhone, setNewPhone] = useState({ title: '', number: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadRestaurantInfo();
  }, []);

  const loadRestaurantInfo = async () => {
    try {
      const data = await fetchRestaurantInfo();
      // Handle both camelCase and PascalCase from API
      let phone = data.phoneNumber || data.PhoneNumber || '';
      // Strip +967 or 00967 or 967 prefix for display
      if (phone.startsWith('+967')) phone = phone.substring(4);
      else if (phone.startsWith('00967')) phone = phone.substring(5);
      else if (phone.startsWith('967')) phone = phone.substring(3);

      setFormData({
        name: data.name || data.Name || '',
        address: data.address || data.Address || '',
        phoneNumber: phone,
        email: data.email || data.Email || '',
        aboutUs: data.aboutUs || data.AboutUs || '',
        ourVision: data.ourVision || data.OurVision || '',
        whatWeProvide: data.whatWeProvide || data.WhatWeProvide || '',
        whyUs: data.whyUs || data.WhyUs || '',
        logoPath: data.logoPath || data.LogoPath || '',
        facebookUrl: data.facebookUrl || data.FacebookUrl || '',
        instagramUrl: data.instagramUrl || data.InstagramUrl || '',
        twitterUrl: data.twitterUrl || data.TwitterUrl || ''
      });

      // Parse additional phones
      try {
        const phones = data.additionalPhones || data.AdditionalPhones;
        if (phones) {
          setAdditionalPhones(typeof phones === 'string' ? JSON.parse(phones) : phones);
        } else {
          setAdditionalPhones([]);
        }
      } catch (e) {
        console.error("Error parsing additional phones", e);
        setAdditionalPhones([]);
      }
      
      const logo = data.logoPath || data.LogoPath;
      if (logo) {
        setLogoPreview(logo.startsWith('http') ? logo : `${config.SITE_BASE_URL}${logo.startsWith('/') ? '' : '/'}${logo}`);
      }
    } catch (error) {
      console.error('Error fetching restaurant info:', error);
      setNotification({ open: true, message: t('loadRestaurantInfoError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      // Allow only numbers and limit to 9 digits
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 9);
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleAddPhone = () => {
    if (newPhone.title && newPhone.number) {
      if (newPhone.number.length !== 9) {
        setNotification({ open: true, message: t('phoneNumberLengthError'), severity: 'error' });
        return;
      }
      const phoneToAdd = {
        title: newPhone.title,
        number: `+967${newPhone.number}`
      };
      setAdditionalPhones([...additionalPhones, phoneToAdd]);
      setNewPhone({ title: '', number: '' });
    }
  };

  const handleRemovePhone = (index) => {
    const updatedPhones = [...additionalPhones];
    updatedPhones.splice(index, 1);
    setAdditionalPhones(updatedPhones);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phoneNumber && formData.phoneNumber.length !== 9) {
      setNotification({ open: true, message: t('phoneNumberLengthError'), severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append('Name', formData.name);
      data.append('Address', formData.address);
      // Prepend +967 to the phone number
      data.append('PhoneNumber', `+967${formData.phoneNumber}`);
      data.append('Email', formData.email);
      data.append('AboutUs', formData.aboutUs);
      data.append('OurVision', formData.ourVision);
      data.append('WhatWeProvide', formData.whatWeProvide);
      data.append('WhyUs', formData.whyUs);
      data.append('FacebookUrl', formData.facebookUrl);
      data.append('InstagramUrl', formData.instagramUrl);
      data.append('TwitterUrl', formData.twitterUrl);
      data.append('AdditionalPhones', JSON.stringify(additionalPhones));

      if (logoFile) {
        data.append('Logo', logoFile);
      }

      await updateRestaurantInfo(data);
      setNotification({ open: true, message: t('restaurantInfoUpdatedSuccess'), severity: 'success' });
    } catch (error) {
      console.error('Error updating restaurant info:', error);
      setNotification({ open: true, message: t('updateRestaurantInfoError'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('restaurantInfoSettings')}
      </Typography>

      <Paper sx={{ p: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab icon={<InfoIcon />} label={t('basicInfo')} />
          <Tab icon={<VisibilityIcon />} label={t('aboutContent')} />
        </Tabs>

        <form onSubmit={handleSubmit}>
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <Avatar
                    src={logoPreview}
                    sx={{ width: 150, height: 150, border: '3px solid #fff', boxShadow: 3 }}
                  />
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'background.default' }
                    }}
                  >
                    <input hidden accept="image/*" type="file" onChange={handleLogoChange} />
                    <PhotoCamera />
                  </IconButton>
                </Box>
                <Typography variant="subtitle1" color="textSecondary">
                  {t('restaurantLogo')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label={t('restaurantName')}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t('phoneNumber')}
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box sx={{ 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              px: 1, 
                              py: 0.5, 
                              borderRadius: 1,
                              mr: 1,
                              fontWeight: 'bold'
                            }}>
                              +967
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      helperText={`${formData.phoneNumber.length}/9 digits`}
                      error={formData.phoneNumber.length > 0 && formData.phoneNumber.length !== 9}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t('email')}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label={t('address')}
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      multiline
                      rows={2}
                    />
                  </Grid>

                  {/* Social Media Section */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{t('socialMedia')}</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Facebook URL"
                          name="facebookUrl"
                          value={formData.facebookUrl}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><FacebookIcon color="primary" /></InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Instagram URL"
                          name="instagramUrl"
                          value={formData.instagramUrl}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><InstagramIcon color="secondary" /></InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Twitter URL"
                          name="twitterUrl"
                          value={formData.twitterUrl}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><TwitterIcon color="info" /></InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Additional Phones Section */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{t('additionalPhones')}</Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 5 }}>
                          <TextField
                            fullWidth
                            label={t('title')}
                            value={newPhone.title}
                            onChange={(e) => setNewPhone({ ...newPhone, title: e.target.value })}
                            size="small"
                            helperText={t('writeNumberOwner')}
                          />
                        </Grid>
                        <Grid size={{ xs: 5 }}>
                          <TextField
                            fullWidth
                            label={t('phoneNumber')}
                            value={newPhone.number}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                              setNewPhone({ ...newPhone, number: val });
                            }}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box sx={{ 
                                    bgcolor: 'primary.main', 
                                    color: 'white', 
                                    px: 0.4, 
                                    py: 0.1, 
                                    borderRadius: 1,
                                    mr: 1,
                                    fontWeight: 'bold',
                                    fontSize: '0.6rem'
                                  }}>
                                    +967
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                            helperText={`${newPhone.number.length}/9`}
                          />
                        </Grid>
                        <Grid size={{ xs: 2 }}>
                          <Button 
                            variant="contained" 
                            onClick={handleAddPhone}
                            disabled={!newPhone.title || !newPhone.number}
                          >
                            <AddIcon />
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                    
                    <List>
                      {additionalPhones.map((phone, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemText primary={phone.title} secondary={phone.number} />
                            <ListItemSecondaryAction>
                              <IconButton edge="end" aria-label="delete" onClick={() => handleRemovePhone(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1 }} /> {t('aboutUs')}
                </Typography>
                <TextField
                  fullWidth
                  name="aboutUs"
                  value={formData.aboutUs}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  placeholder={t('aboutUsPlaceholder')}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <VisibilityIcon sx={{ mr: 1 }} /> {t('ourVision')}
                </Typography>
                <TextField
                  fullWidth
                  name="ourVision"
                  value={formData.ourVision}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  placeholder={t('ourVisionPlaceholder')}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <LocalOfferIcon sx={{ mr: 1 }} /> {t('whatWeProvide')}
                </Typography>
                <TextField
                  fullWidth
                  name="whatWeProvide"
                  value={formData.whatWeProvide}
                  onChange={handleChange}
                  multiline
                  rows={6}
                  placeholder={t('whatWeProvidePlaceholder')}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <ThumbUpIcon sx={{ mr: 1 }} /> {t('whyUs')}
                </Typography>
                <TextField
                  fullWidth
                  name="whyUs"
                  value={formData.whyUs}
                  onChange={handleChange}
                  multiline
                  rows={6}
                  placeholder={t('whyUsPlaceholder')}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <Box sx={{ mt: 4 }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              size="large"
              disabled={saving}
              fullWidth
            >
              {saving ? t('saving') : t('saveChanges')}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}